import axios from 'axios';
import { initAxios } from '../utils/axiosHelper';

// QA/prod set REACT_APP_ANALYZER_URL to an absolute host. Locally it's unset,
// so we fall back to the nginx path proxy at `/analyzer-service` (see
// nginx/nginx.conf) -- same-origin, which keeps the initAxios auth interceptor
// working and avoids a CORS preflight. Same shape as ENTITY_SERVICE_BASE_URL.
// The fallback MUST be a non-empty string: `|| ''` is falsy, so initAxios would
// silently fall through to REACT_APP_API_URL and hit bite-server, which serves
// only part of the ACE surface -- /ace/bulk-file-upload 404s there.
// eslint-disable-next-line no-undef
const ANALYZERS_BASE_URL = (
  process.env.REACT_APP_ANALYZER_URL || '/analyzer-service'
).replace(/\/+$/, '');
// Relative to ANALYZERS_BASE_URL, which getAnalyzersClient() already sets as
// the axios instance's baseURL -- do not repeat it here. Locally that base is
// a relative path (`/analyzer-service`), so axios prepends it and a repeated
// prefix doubles up in the final URL (e.g.
// /analyzer-service/analyzer-service/analyzers/...). In QA/prod the base is an
// absolute host, so axios was silently ignoring baseURL whenever the request
// url was itself absolute -- which happened to mask this same bug there.
const ANALYZERS_API_PREFIX = '/analyzers';

const getAnalyzersClient = () => initAxios(ANALYZERS_BASE_URL);

export interface PresignedUrlResponse {
  uploadUrl: string;
  fileKey: string;
}

export interface AceUploadResponse {
  success: boolean;
  fileKey: string;
  result?: {
    fileKey: string;
  };
}

export type AceTariffMatchMode = 'applied' | 'recommends' | 'either';
export interface AceTariffMatch {
  prefix: string;
  mode: AceTariffMatchMode;
}

// Bulk-action criteria. `tariffMatches` is the current per-program shape;
// `tariffPrefixes` remains accepted server-side (treated as applied) for
// any legacy caller.
/**
 * The run-details table's current view — its tab, search box and filter chips,
 * in the same shape the lines endpoint takes.
 *
 * Sent with a bulk action so the action applies to exactly the rows on screen.
 */
export interface AceViewScope {
  filterTab?: string;
  search?: string;
  tariffFilters?: string;
  findingFilters?: string;
  countryFilters?: string;
}

export interface AceBulkFilter {
  tariffMatches?: AceTariffMatch[];
  tariffPrefixes?: string[];
  flagTypes?: string[];
  countryCodes?: string[];
  /**
   * Restricts the action to the rows the table is currently showing.
   * INTERSECTS with the criteria above rather than replacing them — search an
   * HTS code, then clear missing-ch99 findings, and only those search results
   * are affected. Omit to act on the whole run.
   */
  viewScope?: AceViewScope;
}

export interface AceAnalysisStartResponse {
  success: boolean;
  message: string;
  runId: string;
  jobId: string;
}

export interface IeepaReportResponse {
  success: boolean;
  url: string | null;
  fileName: string | null;
  totalOutputRows: number;
  message?: string;
}

/**
 * Options bag for {@link AnalyzersAPI.generateIeepaReport}. All fields are
 * optional so older call sites that just pass a runId still compile.
 *
 * - rulesMode: primary discriminator for the three IEEPA export variants.
 *     'eligible'   (default) — CAPE Phase I Eligibility Report. Rule 1
 *                   (must carry an IEEPA tariff hit) PLUS Rules 2–11.
 *     'all'                  — Rule 1 only. Every IEEPA-bearing row
 *                   regardless of eligibility. Same schema as 'eligible'.
 *     'ineligible'           — Rule 1 AND NOT(Rules 2–11). Every
 *                   IEEPA-bearing row that is EXCLUDED from Phase I for
 *                   failing at least one eligibility rule. Useful for
 *                   auditing what the Phase I filter drops.
 *     'phase_2'              — CAPE Phase II Claim Eligibility. Same hard
 *                   gate as 'eligible', but every surviving line is also
 *                   classified Claimable vs Needs-Review and carries
 *                   trade-remedy / AD-CVD / refund-validation flags plus
 *                   ES-003 verification-gap disclosures. Needs-Review
 *                   lines are included (flagged), not dropped.
 *     'phase_2_exclusions'   — the Phase II complement: Rule 1 AND
 *                   NOT(Rules 2–11) in the Phase II column layout.
 *     'phase_2_rollup'       — CAPE Phase II Entry Rollup. One row per entry
 *                   summary aggregating the claimable-scope lines: line
 *                   counts by status, total IEEPA duty / refund, AD-CVD
 *                   totals (kept separate), duplicate detection, and
 *                   entry-level reconciliation. The claim unit a preparer
 *                   assembles.
 *
 * - applyRules: legacy boolean kept for backwards compatibility with the
 *   original two-mode UI. true  -> 'eligible'. false -> 'all'. If both
 *   applyRules and rulesMode are set, rulesMode wins.
 *
 * - customFileName: attachment filename the browser should see. The
 *   backend sanitizes it and appends .csv if missing, so callers can pass
 *   the raw string the user typed.
 */
export interface GenerateIeepaReportOptions {
  rulesMode?:
    | 'eligible'
    | 'all'
    | 'ineligible'
    | 'phase_2'
    | 'phase_2_exclusions'
    | 'phase_2_rollup';
  applyRules?: boolean;
  customFileName?: string;
}

export class AnalyzersAPI {
  /**
   * Requests a secure, temporary S3 URL to upload the raw ACE document directly from the browser.
   */
  static async getAcePresignedUrl(
    runName: string,
    groupId: string,
    teamName: string,
    fileName?: string,
  ): Promise<PresignedUrlResponse> {
    const response = await getAnalyzersClient().get(
      `${ANALYZERS_API_PREFIX}/ace/presigned-url`,
      {
        params: {
          runName: runName,
          groupId: groupId,
          teamName: teamName,
          // Older deployments of this endpoint ignore the extra param and fall
          // back to an "original.csv" key, so this degrades cleanly.
          fileName: fileName || undefined,
        },
      },
    );
    return response.data;
  }

  /**
   * Uploads the raw ACE CSV straight to S3 with the presigned PUT URL.
   *
   * Deliberately uses a bare `axios` call rather than getAnalyzersClient():
   * the presigned query signature IS the credential, and S3 rejects a request
   * that also carries an Authorization header. `initAxios` builds a fresh
   * instance via axios.create, so the default export has no interceptors.
   *
   * The Content-Type MUST match what the URL was signed with ('text/csv' —
   * see generatePresignedUrl) or S3 fails the signature check. timeout is
   * disabled because a 100-200 MB ES-003 roll-up can take minutes.
   */
  static async uploadAceFileToS3(uploadUrl: string, file: File): Promise<void> {
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': 'text/csv' },
      timeout: 0,
    });
  }

  /**
   * Legacy multipart upload. Buffers the whole file in the analyzer service's
   * heap and is capped at 25 MB server-side, so real ACE files must go through
   * getAcePresignedUrl + uploadAceFileToS3 instead. Kept for callers that
   * upload small files.
   */
  static async uploadAceFile(formData: FormData): Promise<AceUploadResponse> {
    const response = await getAnalyzersClient().post(
      `${ANALYZERS_API_PREFIX}/ace/bulk-file-upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  }

  static async getBiteReportDownloadUrl(
    runId: string,
    fileName: string,
  ): Promise<{ url: string }> {
    return AnalyzersAPI.getS3DownloadUrl(runId, 'bite_report', fileName);
  }

  /**
   * Notifies the server that the upload is complete and triggers the RabbitMQ background job.
   */
  static async startAceAnalysis(
    name: string,
    s3FileKey: string,
    groupId: string,
    spendMode: 'trial' | 'active',
    initiatedBy?: {
      id?: string | null;
      name?: string | null;
      email?: string | null;
    },
    sourceFormat: 'es003' | 'cargowise' = 'es003',
    effectiveDate?: string | null,
    excludeIeepa = false,
  ): Promise<AceAnalysisStartResponse> {
    // The initiated_by fields are an attribution snapshot for the asynchronous
    // worker. They are not authorization inputs; the analyzer service carries
    // them on the queued ticket until the eventual credit spend.
    // source_format = 'cargowise' processes a CargoWise export (already 1 row
    // per line); effective_date is the user-picked HTS rate date it needs.
    const response = await getAnalyzersClient().post(
      `${ANALYZERS_API_PREFIX}/ace/start`,
      {
        name: name,
        s3_file_key: s3FileKey,
        group_id: groupId,
        reRun: false,
        spendMode: spendMode,
        initiated_by_name: initiatedBy?.name || undefined,
        initiated_by_id: initiatedBy?.id || undefined,
        initiated_by_email: initiatedBy?.email || undefined,
        source_format: sourceFormat,
        effective_date: effectiveDate || undefined,
        // Runs the analysis with every IEEPA rule removed from the engine, so
        // no IEEPA code is expected on any line. Same idea as the Tariff Tool's
        // "hide IEEPA" toggle.
        exclude_ieepa: excludeIeepa,
      },
      { timeout: 300_000 },
    );
    return response.data;
  }

  static async getAceRuns(groupId: string): Promise<any[]> {
    const response = await getAnalyzersClient().get(
      `${ANALYZERS_API_PREFIX}/ace/runs`,
      {
        params: { groupId: groupId },
      },
    );
    return response.data;
  }

  static async getAceRunDetails(
    runId: string,
    tariffFilters?: string,
    findingFilters?: string,
    countryFilters?: string,
  ): Promise<any> {
    const response = await getAnalyzersClient().get(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}`,
      {
        params: {
          tariffFilters: tariffFilters,
          findingFilters: findingFilters,
          countryFilters: countryFilters,
        },
      },
    );
    return response.data;
  }

  static async getAceRunLines(
    runId: string,
    page: number,
    limit: number,
    filterTab?: string,
    search?: string,
    tariffFilters?: string,
    findingFilters?: string,
    countryFilters?: string,
    sortBy?: string,
    sortDir?: string,
  ): Promise<{ lines: any[]; total: number }> {
    const response = await getAnalyzersClient().get(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/lines`,
      {
        params: {
          page: page,
          limit: limit,
          filter: filterTab,
          search: search,
          tariffFilters: tariffFilters,
          findingFilters: findingFilters,
          countryFilters: countryFilters,
          sortBy: sortBy || undefined,
          sortDir: sortDir || undefined,
        },
      },
    );
    return response.data;
  }

  static async getAceUnmatchedRows(
    runId: string,
    page = 1,
    limit = 50,
    search?: string,
    sortBy?: string,
    sortDir?: string,
  ): Promise<{ rows: any[]; total: number }> {
    const response = await getAnalyzersClient().get(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/unmatched`,
      {
        params: {
          page: page,
          limit: limit,
          search: search || undefined,
          sortBy: sortBy || undefined,
          sortDir: sortDir || undefined,
        },
      },
    );
    return response.data;
  }

  static async deleteAceRun(
    runId: string,
  ): Promise<{ success: boolean; message?: string }> {
    const response = await getAnalyzersClient().delete(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}`,
    );
    return response.data;
  }

  /**
   * Update the user-visible status of a run. Backend whitelists the value
   * to 'Completed' or 'Needs Review' and only applies the change to runs
   * already in one of those states.
   */
  static async updateAceRunStatus(
    runId: string,
    status: 'Completed' | 'Needs Review',
  ): Promise<{ success: boolean; run?: { id: string; status: string } }> {
    const response = await getAnalyzersClient().put(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/status`,
      { status: status },
    );
    return response.data;
  }

  static async getS3DownloadUrl(
    runId: string,
    docType: string,
    fileName?: string,
  ): Promise<{ url: string }> {
    const response = await getAnalyzersClient().get(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/download`,
      {
        params: {
          type: docType,
          fileName: fileName,
        },
      },
    );
    return response.data;
  }

  /**
   * Kicks off backend IEEPA report generation.
   *
   * The server opens a Postgres cursor over IEEPA rows, runs the top-up /
   * refund math per row, and streams CSV bytes straight into an S3 multipart
   * upload. Instead of returning the CSV payload, it responds with a
   * short-lived presigned GET URL that the browser opens directly — no large
   * data set ever crosses this client.
   *
   * Uses a per-call 5-minute timeout because a very large run (hundreds of
   * thousands of IEEPA hits) can take tens of seconds to stream. The
   * instance-wide axios default (commonly 30s / 60s) would abort the request
   * while the server is still writing parts to S3 and leave the UI in an
   * error state even though the file actually got built.
   *
   * @param runId - the ACE analyzer run to export
   * @param options - see {@link GenerateIeepaReportOptions}. When omitted,
   *   the backend applies the full eligibility rule set (CAPE Phase I
   *   Eligibility Report) and picks a default attachment name.
   *
   * Returns `{ url: null, totalOutputRows: 0 }` when the run has no
   * qualifying IEEPA rows — handler should surface a friendly info message
   * rather than triggering an empty download.
   */
  static async generateIeepaReport(
    runId: string,
    options?: GenerateIeepaReportOptions,
  ): Promise<IeepaReportResponse> {
    // Body mirrors the service options bag 1:1. Stays an empty object if no
    // options were passed so older backend builds that don't read the body
    // still behave identically to the previous rules-applied behavior.
    const body: Record<string, unknown> = {};
    if (options?.rulesMode !== undefined) {
      body.rulesMode = options.rulesMode;
    }
    if (options?.applyRules !== undefined) {
      body.applyRules = options.applyRules;
    }
    if (options?.customFileName !== undefined) {
      body.customFileName = options.customFileName;
    }

    const response = await getAnalyzersClient().post(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/ieepa-report`,
      body,
      { timeout: 300_000 },
    );
    return response.data;
  }

  /**
   * Re-processes an existing run from its original upload.
   *
   * `excludeIeepa` is optional on purpose: omit it to re-run with whatever
   * IEEPA setting the run already has, or pass a boolean to switch it. The
   * server persists the new value on the run, so the change sticks.
   */
  static async reRunAceAnalysis(
    runId: string,
    s3FileKey: string,
    groupId: string,
    excludeIeepa?: boolean,
    initiatedBy?: {
      id?: string | null;
      name?: string | null;
      email?: string | null;
    },
  ): Promise<AceAnalysisStartResponse> {
    const response = await getAnalyzersClient().post(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/re-run`,
      {
        s3_file_key: s3FileKey,
        group_id: groupId,
        reRun: true,
        exclude_ieepa: excludeIeepa,
        initiated_by_name: initiatedBy?.name || undefined,
        initiated_by_id: initiatedBy?.id || undefined,
        initiated_by_email: initiatedBy?.email || undefined,
      },
    );
    return response.data;
  }

  /**
   * Bulk Actions — apply clear / restore / comment / review to every row
   * matching the filter. Tariffs are matched per-program with a MATCH
   * MODE (applied = customer filed it, recommends = BITE expected it,
   * either = both) so the user can target, say, "every line where Sec 232
   * Steel is applied OR recommended". Flag types and country ISOs are
   * AND'd against the tariff group. Server returns rowsAffected for the
   * confirmation toast.
   */
  /**
   * clearScope: 'all' clears/restores every finding on each matched row;
   * 'selected' limits the action to the finding types in filter.flagTypes.
   * It MUST be sent in the body — the server defaults an absent value to
   * 'all', so dropping it silently turns the modal's scope choice into a
   * no-op that clears more than the user asked for.
   */
  /**
   * clearScope: 'all' clears/restores every finding on each matched row;
   * 'selected' limits the action to the finding types in filter.flagTypes.
   * It MUST be sent in the body — the server defaults an absent value to
   * 'all', so dropping it silently turns the modal's scope choice into a
   * no-op that clears more than the user asked for.
   */
  static async bulkClearAceFindings(
    runId: string,
    filter: AceBulkFilter,
    // 'all' clears every finding on matched rows; 'selected' clears only
    // the finding types in filter.flagTypes.
    clearScope: 'all' | 'selected' = 'all',
  ): Promise<{ success: boolean; rowsAffected: number }> {
    const response = await getAnalyzersClient().post(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/bulk/clear`,
      // clearScope MUST be sent: the server defaults an absent value to the
      // destructive 'all', so dropping it silently turns the modal's scope
      // choice into a no-op that clears more than the user asked for.
      { filter: filter, clearScope: clearScope },
    );
    return response.data;
  }

  static async bulkRestoreAceFindings(
    runId: string,
    filter: AceBulkFilter,
    clearScope: 'all' | 'selected' = 'all',
  ): Promise<{ success: boolean; rowsAffected: number }> {
    const response = await getAnalyzersClient().post(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/bulk/restore`,
      // clearScope MUST be sent: the server defaults an absent value to the
      // destructive 'all', so dropping it silently turns the modal's scope
      // choice into a no-op that clears more than the user asked for.
      { filter: filter, clearScope: clearScope },
    );
    return response.data;
  }

  static async bulkAddAceLineComment(
    runId: string,
    text: string,
    filter: AceBulkFilter,
  ): Promise<{ success: boolean; rowsAffected: number }> {
    const response = await getAnalyzersClient().post(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/bulk/comment`,
      { text: text, filter: filter },
    );
    return response.data;
  }

  /**
   * Bulk set/clear the reviewed status on matching rows — status control
   * that doesn't touch findings.
   */
  static async bulkSetAceReviewed(
    runId: string,
    filter: AceBulkFilter,
    reviewed: boolean,
  ): Promise<{ success: boolean; rowsAffected: number }> {
    const response = await getAnalyzersClient().post(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/bulk/reviewed`,
      { filter: filter, reviewed: reviewed },
    );
    return response.data;
  }

  /**
   * Read-only preview of how many transactions a filter would hit. Powers
   * the modal's live "will affect ~N" readout.
   */
  static async bulkPreviewAceCount(
    runId: string,
    filter: AceBulkFilter,
  ): Promise<{ success: boolean; count: number }> {
    const response = await getAnalyzersClient().post(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/bulk/preview-count`,
      { filter: filter },
    );
    return response.data;
  }

  /**
   * Catalog of filter options actually present on this run — tariff
   * prefixes, flag types, and country-of-origin ISOs. The UnifiedFilter
   * modal calls this on open so it only shows checkboxes for options
   * the run can legitimately be filtered by.
   */
  static async getAceRunFilterOptions(runId: string): Promise<{
    tariffPrefixes: string[];
    flagTypes: string[];
    countries: string[];
  }> {
    const response = await getAnalyzersClient().get(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/filter-options`,
    );
    return response.data;
  }

  static async markLineAsReviewed(
    runId: string,
    lineId: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await getAnalyzersClient().put(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/lines/${lineId}/review`,
    );
    return response.data;
  }

  static async addLineNote(
    runId: string,
    lineId: string,
    text: string,
    user: string,
    userId?: string | null,
  ): Promise<{ success: boolean; note: any }> {
    // `user` is kept for backwards compatibility but the server now
    // resolves attribution from the auth context. user_name/user_id are
    // a client-side fallback the server only uses when token claims are
    // empty (see AnalyzersController.extractUserFromReq).
    const response = await getAnalyzersClient().post(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/lines/${lineId}/notes`,
      {
        text: text,
        user: user,
        user_name: user,
        user_id: userId || undefined,
      },
    );
    return response.data;
  }

  static async clearAceLineFindings(
    runId: string,
    lineId: string,
  ): Promise<any> {
    const response = await getAnalyzersClient().put(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/lines/${lineId}/clear-findings`,
    );
    return response.data;
  }

  /**
   * Non-destructive per-finding clear. Pass an array of flag signatures
   * (`${type}|${message}` from the drawer's per-flag X button) to clear
   * specific findings, or omit signatures (or pass undefined) to clear
   * every active finding on the row. Server returns the full
   * `cleared_flags` array so the UI can re-render without re-fetching
   * the line. Identity-of-clearer is captured server-side from the auth
   * token.
   */
  static async clearAceLineFindingsBySignature(
    runId: string,
    lineId: string,
    signatures?: string[],
  ): Promise<{
    success: boolean;
    line: any;
    cleared_flags: Array<{
      signature: string;
      clearedByUserId: string | null;
      clearedByName: string;
      clearedAt: string;
    }>;
  }> {
    const response = await getAnalyzersClient().post(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/lines/${lineId}/findings/clear`,
      { signatures: signatures },
    );
    return response.data;
  }

  /**
   * Restore previously-cleared findings. Pass specific signatures to
   * restore those only, omit signatures to restore everything (the
   * "Restore all findings" path).
   */
  static async restoreAceLineFindingsBySignature(
    runId: string,
    lineId: string,
    signatures?: string[],
  ): Promise<{
    success: boolean;
    line: any;
    cleared_flags: Array<{
      signature: string;
      clearedByUserId: string | null;
      clearedByName: string;
      clearedAt: string;
    }>;
  }> {
    const response = await getAnalyzersClient().post(
      `${ANALYZERS_API_PREFIX}/ace/runs/${runId}/lines/${lineId}/findings/restore`,
      { signatures: signatures },
    );
    return response.data;
  }
}
